//Proofed
import FilterListIcon from '@mui/icons-material/FilterList'
import DescriptionIcon from '@mui/icons-material/Description'
import SchoolIcon from '@mui/icons-material/School'
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'
import SearchIcon from '@mui/icons-material/Search'
import QueryStatsIcon from '@mui/icons-material/QueryStats'
import type { HowItWorksStep } from '../components/HowItWorksSection'

// Hero right-panel highlights
export const newToGovHighlights = [
    'Identify which contracts suit you as a new supplier',
    'Understand how buyers procure and what they expect from first-time suppliers',
    'Learn what a compliant, competitive bid looks like',
    'Receive expert guidance priot to your first submission through a Bid Prep Day',
    'Build a strategy to tackle the public sector, starting with the right opportunities',
]

// Challenge cards  section 2
export const newToGovChallenges = [
    {
        heading: "You don't know which contracts are right for your stage",
        body: "The public sector market is huge and most new suppliers waste time on opportunities that are too large, too competitive, or outside their reach. Finding the right entry point is the first challenge",
    },
    {
        heading: "You have no track record. How do you win your first contract?",
        body: "Buyers want evidence. Case studies, references, past performance but you can't build a track record without winning. We help you find and target the opportunities where new entrants can genuinely compete",
    },
    {
        heading: "You don't know what buyers expect from a first-time supplier",
        body: "Compliant structure, evidence of capability, social value statements, pricing approach: public sector bids have conventions that aren't obvious. Getting these wrong is expensive",
    },
]

// Recommended solution stack  section 3
export const newToGovSolutionStack = [
    {
        Icon: SearchIcon,
        title: 'CFP Core Pro',
        body: 'Find the right opportunities early. Set up targeted alerts by sector, value, location and keyword so you never miss a tender relevant to your business. Research buyers and benchmark what competitors are winning',
        tag: 'Platform',
        link: '/products/core',
        linkLabel: 'Learn about Core',
    },
    {
        Icon: QueryStatsIcon,
        title: 'Market Research Paper',
        body: 'A targeted advisory report on your sector and the buyers most likely to purchase what you do. Covers procurement patterns, typical contract values, key frameworks and where new entrants have historically succeeded',
        tag: 'Advisory',
        link: '/products/bid-services',
        linkLabel: 'Learn about Bid Services',
    },
    {
        Icon: SchoolIcon,
        title: 'Bid Prep Day',
        body: 'A structured advisory session that identifies exactly what you need to be ready to bid: registration requirements, what buyers look for, how to structure your first response and the steps to your first win',
        tag: 'Advisory',
        link: '/products/bid-services',
        linkLabel: 'Learn about Bid Services',
    },
]

// Journey steps  section 4
export const newToGovSteps: HowItWorksStep[] = [
    {
        num: '1',
        Icon: FilterListIcon,
        title: 'Set Up Core Pro',
        body: 'Configure your sector filters, value thresholds and keyword alerts. From day one you have a daily digest of relevant opportunities',
    },
    {
        num: '2',
        Icon: QueryStatsIcon,
        title: 'Understand the Market',
        body: 'Your Market Research paper maps the landscape: key buyers, typical contract sizes, procurement methods and where new suppliers have won before',
    },
    {
        num: '3',
        Icon: SchoolIcon,
        title: 'Complete Bid Prep Day',
        body: 'Our advisory team runs you through exactly what buyers want to see. You leave knowing what you need, what to say and how to structure a compliant, competitive response',
    },
    {
        num: '4',
        Icon: EmojiEventsIcon,
        title: 'Submit and Start Building',
        body: 'Apply to your first targeted opportunity with confidence. Every submission builds your track record and refines your approach',
    },
]

// Outcomes checklist  section 5
export const newToGovOutcomes = [
    'Identify contracts at the right scale and value for a new supplier, with no wasted effort on tenders you cannot win',
    "Understand how buyers go to market, who they've been buying from and what they value in a supplier",
    'Know what a compliant, competitive bid looks like before you write a single word',
    'Have a structured daily process so nothing slips past while you focus on running your business',
    'Build your track record steadily, starting with the opportunities where new entrants genuinely compete',
]

// Self-qualification checklist  section 6
export const newToGovQualifiers = [
    "You're new to UK & Ireland public sector procurement or have tried before without a structured approach",
    "You have a product or service that government actually buys",
    "You want a structured route in, not trial and error over 12 months",
    "You don't have an in-house bid team yet or you do but they've never written a public sector bid",
    "You're serious about building a long-term public sector revenue stream",
]



// FAQs  section 8
export const newToGovFaqs = [
    {
        q: 'Do I need existing government contracts to apply for public sector work?',
        a: "No, but you do need to demonstrate capability. Buyers will want evidence that you can deliver: case studies, accreditations, financial stability, or references from comparable private sector work. Our Bid Prep Day helps you understand exactly what evidence you need to build before your first submission.",
    },
    {
        q: 'What is a Bid Prep Day?',
        a: "A Bid Prep Day is a structured advisory session with our team. We assess your current position, identify gaps in your readiness (registration, compliance, case studies, pricing approach) and produce a clear action plan for your first submission. It typically takes a full working day, either remote or in person.",
    },
    {
        q: 'How long before I can submit my first bid?',
        a: "It depends on your starting point. For businesses with some case studies and relevant experience, it can be as little as 4-8 weeks. For businesses starting from scratch, 3-6 months is more realistic to build the right foundations. We'll give you a clear timeline on the discovery call.",
    },
    {
        q: 'What contracts should I target as a new supplier?',
        a: "New entrants typically do best with lower-value, shorter-duration contracts, often below the OJEU/Find a Tender threshold, where buyers have more flexibility and less requirement for extensive track records. The Market Research paper will identify the specific opportunities most suitable for your business.",
    },
    {
        q: "What's in the Market Research paper?",
        a: "A targeted intelligence report covering: the buyers most active in your sector, their procurement patterns and typical contract values, which frameworks and DPS arrangements are relevant to you, who the established suppliers are and where new entrants have previously succeeded. It gives you a strategic foundation before you bid.",
    },
    {
        q: 'Can I just start with CFP Core Professional and add advisory services later?',
        a: "Yes and many clients do. CFP Core Professional is the foundation: it gives you live opportunity monitoring and buyer intelligence from day one. Advisory services like the Market Research paper and Bid Prep Day are add-ons you can commission when you're ready. That said, doing them early saves significant wasted effort.",
    },
]

// Related solution links  section 9
export const newToGovRelated = [
    {
        headline: 'Win More Tenders',
        body: 'Already bidding but want better win rates and higher throughput? The next step is improving efficiency with Tender Ninja alongside Core Professional',
        link: '/solutions/grow-faster-uk-public-sector',
        tag: 'Established Supplier',
    },
    {
        headline: 'Enter New Markets',
        body: 'Growing fast and expanding into multiple sectors? Multi-licence Core Pro, Tender Ninja and advisory support for businesses scaling their public sector reach',
        link: '/solutions/expand-into-new-sectors',
        tag: 'Expanding Reach',
    },
]
